import type { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { mergeStoredFilters } from '@src/packages/utils/hooks/usePersistedFilters';
import { translateString } from '@src/util/intl/translateString';

import {
  formValuesFactory,
  generateNameByForms,
  getValuesFromDynamicForm,
  getValuesFromTaskForms,
  INPUT_TYPES_NOT_ALLOWED,
  isWhenShouldOccurDisabledByTemplate,
  validateTaskForms,
  WHEN_SHOULD_OCCUR_VALUES,
  workOrderFactory,
  WORK_FORM_REGEX,
} from '@src/models/mappers/workOrder.mapper';

export {
  formValuesFactory,
  generateNameByForms,
  getValuesFromDynamicForm,
  getValuesFromTaskForms,
  INPUT_TYPES_NOT_ALLOWED,
  isWhenShouldOccurDisabledByTemplate,
  validateTaskForms,
  WHEN_SHOULD_OCCUR_VALUES,
  workOrderFactory,
  WORK_FORM_REGEX,
};

export const BASE_URL = 'work_orders';

const DETAILS_RELATIVE = 'details/:divId/:orderId';

export const PATHS = {
  BASE_URL,
  CREATE_ORDER: `${BASE_URL}/create`,
  CREATE_ORDER_BY_TEMPLATE: `${BASE_URL}/create/:divId/:templateId`,
  EDIT_ORDER: `${BASE_URL}/order/:divId/:orderId`,
  DETAILS: `${BASE_URL}/${DETAILS_RELATIVE}`,
  DETAILS_RELATIVE,
};

export const FORM_ITEM_LAYOUT = {
  labelCol: {
    span: 6,
  },
  wrapperCol: {
    span: 24,
  },
};

export const FORMS = {
  WORK_ORDER: 'workOrder',
  WORK_FORM: 'work_form',
  TASK_FORM: 'task',
  MEMBERS: 'members',
};

export const MEMBER_TYPES = {
  USER: 'user',
  GROUP: 'group',
};

/**
 * @deprecated Read `ExecutionState` from `@src/models/execution` instead.
 */
const STATUS = {
  ACTIVE: 'active',
  STALLED: 'stalled',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
  INCOMPLETE: 'incomplete',
  UNASSIGNED: 'unassigned',
  INACTIVE: 'inactive',
  COMPLETE_EXPIRED: 'complete_expired',
  INCOMPLETE_PARTIAL: 'incomplete_partial',
  CANCELED: 'canceled',
};

export const WORK_ORDER_STATUS = [
  STATUS.ACTIVE,
  STATUS.STALLED,
  STATUS.COMPLETED,
  STATUS.EXPIRED,
  STATUS.INCOMPLETE,
  STATUS.UNASSIGNED,
  STATUS.INACTIVE,
  STATUS.COMPLETE_EXPIRED,
  STATUS.INCOMPLETE_PARTIAL,
  STATUS.CANCELED,
];

export const STATUS_ALLOWED_TO_REFRESH = [STATUS.ACTIVE, STATUS.UNASSIGNED];

/**
 * How often the Elastic id window and quick-filter counts refresh while the
 * realtime list is mounted. Paused while the tab is hidden.
 */
export const WORK_ORDERS_LIST_REFRESH_INTERVAL_MS = 45_000;

/**
 * Realtime list keeps tasks whose `endAt` is after `now - window` (rolling).
 * Older finished work stays out of "Tareas en tiempo real".
 */
export const WORK_ORDERS_REALTIME_ENDED_WITHIN_MS = 24 * 60 * 60 * 1000;

export const WORK_ORDER_QUICK_FILTER = {
  ALL: 'all',
  UNASSIGNED: 'unassigned',
  ACTIVE: 'active',
  STALLED: 'stalled',
  EXPIRED: 'expired',
} as const;

export type WorkOrderQuickFilterKey =
  (typeof WORK_ORDER_QUICK_FILTER)[keyof typeof WORK_ORDER_QUICK_FILTER];

export const WORK_ORDER_QUICK_FILTER_STATUSES: Record<WorkOrderQuickFilterKey, string[]> = {
  [WORK_ORDER_QUICK_FILTER.ALL]: [...WORK_ORDER_STATUS],
  [WORK_ORDER_QUICK_FILTER.UNASSIGNED]: [STATUS.UNASSIGNED],
  [WORK_ORDER_QUICK_FILTER.ACTIVE]: [STATUS.ACTIVE],
  [WORK_ORDER_QUICK_FILTER.STALLED]: [STATUS.STALLED],
  [WORK_ORDER_QUICK_FILTER.EXPIRED]: [STATUS.EXPIRED, STATUS.COMPLETE_EXPIRED],
};

export const PAGE_SIZE = 25;

/** Product default; a stored page size on this view wins after the first visit. */
export const WORK_ORDERS_LIST_DEFAULT_PAGE_SIZE = PAGE_SIZE;

export const WORK_ORDER_LIST_DEFAULT_ELASTIC_SORT = {
  field: 'urgency',
  order: 'asc' as const,
};

export const WORK_ORDER_LIST_ELASTIC_SORT_FIELDS: Record<string, string> = {
  startTz: 'startAt._seconds',
  status: 'currentState',
};

export const WORK_ORDERS_LIST_VIEW_STORAGE_KEY = 'workOrdersList';

export interface WorkOrderFilterField<T> {
  value: T;
  label: string;
  valueType: FilterValues[string]['valueType'];
  clearValue: T;
  valueTranslated?: string;
  /** Hide the active-filter tag while `value === clearValue` (see `FilterActiveTags`). */
  hideWhenCleared?: boolean;
}

/** Object type, not an interface: it must stay assignable to `FilterValues`. */
export type WorkOrderFilters = {
  name: WorkOrderFilterField<string>;
  templateIds: WorkOrderFilterField<string[]>;
  users: WorkOrderFilterField<string[]>;
  status: WorkOrderFilterField<string[]>;
};

export const INIT_FILTERS: WorkOrderFilters = {
  name: {
    value: '',
    label: translateString('general.name'),
    valueType: 'string',
    clearValue: '',
  },
  templateIds: {
    value: [],
    label: translateString('work.order.info.template'),
    valueType: 'array',
    clearValue: [],
  },
  users: {
    value: [],
    label: translateString('general.user'),
    valueType: 'array',
    clearValue: [],
  },
  status: {
    value: [],
    label: translateString('general.status'),
    valueType: 'array',
    clearValue: [],
  },
};

/**
 * Drops the fields the list no longer carries — `dates` and its successor `period` (no
 * calendar window on this list) and the free-text `templateName`. Stripping them is
 * load-bearing: a blob written while those filters existed would otherwise restore their
 * active tags. Everything that remains is primitive or `string[]`, which
 * `mergeStoredFilters` carries across on its own.
 */
export const normalizePersistedWorkOrderListFilters = (
  stored: unknown,
  defaults: FilterValues,
): FilterValues => {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return mergeStoredFilters(stored, defaults);
  }

  const {
    dates: _legacyDates,
    period: _legacyPeriod,
    templateName: _legacyTemplateName,
    ...filters
  } = stored as Record<string, unknown>;

  const merged = mergeStoredFilters(filters, defaults);
  const status = merged.status as WorkOrderFilterField<string[]> | undefined;
  if (status && isAllStatusSelection(status.value)) {
    return {
      ...merged,
      status: { ...defaults.status, value: [], valueTranslated: undefined },
    };
  }

  return merged;
};

const isFilterValuesFormat = (filters: Record<string, unknown>): boolean =>
  Boolean(
    filters?.name &&
    typeof filters.name === 'object' &&
    filters.name !== null &&
    'value' in filters.name,
  );

const isAllStatusSelection = (statuses: string[] | undefined): boolean => {
  if (!statuses?.length) return true;
  if (statuses.length !== WORK_ORDER_STATUS.length) return false;
  const statusSet = new Set(statuses);
  return WORK_ORDER_STATUS.every(status => statusSet.has(status));
};

/** Empty status = no chip. A stored "all statuses" / Todos selection is the same. */
const normalizeStatusFilterField = (
  storedStatus?: WorkOrderFilterField<string[]>,
): WorkOrderFilterField<string[]> => {
  const value = storedStatus?.value;
  if (isAllStatusSelection(value)) {
    return { ...INIT_FILTERS.status };
  }

  return { ...INIT_FILTERS.status, ...storedStatus, value: value ?? [] };
};

export const normalizeStoredWorkOrderFilters = (
  stored: Record<string, unknown> | null | undefined,
): WorkOrderFilters => {
  if (!stored) {
    return INIT_FILTERS;
  }

  if (isFilterValuesFormat(stored)) {
    // `templateName` was a free-text filter; the template picker replaced it, so
    // a stored value is dropped instead of surviving as an orphan filter chip.
    // `dates` and its successor `period` are gone: the list has no calendar window.
    const {
      templateName: _legacyTemplateName,
      dates: _legacyDates,
      period: _legacyPeriod,
      ...storedFilters
    } = stored;
    const storedName = storedFilters.name as WorkOrderFilterField<string>;
    const storedTemplateIds = storedFilters.templateIds as
      | WorkOrderFilterField<string[]>
      | undefined;
    const storedUsers = storedFilters.users as WorkOrderFilterField<string[]> | undefined;
    const storedStatus = storedFilters.status as WorkOrderFilterField<string[]> | undefined;

    return {
      ...INIT_FILTERS,
      ...storedFilters,
      name: { ...INIT_FILTERS.name, ...storedName },
      templateIds: { ...INIT_FILTERS.templateIds, ...(storedTemplateIds ?? {}) },
      users: { ...INIT_FILTERS.users, ...(storedUsers ?? {}) },
      status: normalizeStatusFilterField(storedStatus),
    };
  }

  return {
    ...INIT_FILTERS,
    name: { ...INIT_FILTERS.name, value: (stored.name as string) ?? '' },
    templateIds: {
      ...INIT_FILTERS.templateIds,
      value: (stored.templateIds as string[]) ?? [],
    },
    users: { ...INIT_FILTERS.users, value: (stored.users as string[]) ?? [] },
    status: normalizeStatusFilterField({
      ...INIT_FILTERS.status,
      value: (stored.status as string[]) ?? [],
    }),
  };
};

/** Same limit the mobile app enforces on the delay justification. */
export const DELAY_REASON_MAX_LENGTH = 100;

/** Supervisor closure reason (ADR 0002): room for "done on paper, signed sheet #123". */
export const SUPERVISOR_CLOSE_REASON_MAX_LENGTH = 200;

export const TASK_DETAILS_TABS = {
  GENERAL: 'general',
  MEMBERS: 'members',
  HISTORY: 'history',
  DELAYS: 'delays',
};

export const getRouteToCreateWorkOrder = (): string => PATHS.CREATE_ORDER;
export const getRouteToDetails = (divId: string, orderId: string): string =>
  PATHS.DETAILS.replace(':divId', divId).replace(':orderId', orderId);
export const getToWorkOrderList = (orgId: string): string => `/${orgId}/-/${PATHS.BASE_URL}`;

/** Deep link that pre-filters the work orders list down to a single execution. */
export const getRouteToWorkOrdersByExecution = ({
  executionId,
  divId,
}: {
  executionId?: string;
  divId?: string;
}): string => {
  if (!executionId || !divId) return PATHS.BASE_URL;

  const params = new URLSearchParams();
  params.set('executionId', executionId);
  params.set('divId', divId);

  return `${PATHS.BASE_URL}?${params.toString()}`;
};

export const buildQueryWithFilters = <T>(queryByDivId: Record<string, T>): Record<string, T> => {
  const queryWithFilters: Record<string, T> = {};
  Object.keys(queryByDivId).forEach(divId => {
    queryWithFilters[divId] = queryByDivId[divId];
  });

  return queryWithFilters;
};

export interface TaskDetailsData {
  general: Record<string, unknown>;
  members: { users: unknown[]; groups: unknown[] };
  eventTypes: unknown[];
  reception: null;
  timeline: null;
}

export const taskDetailsFactory = (
  mainData: Record<string, unknown> | null,
): TaskDetailsData | null => {
  if (!mainData) return null;

  const schedule = mainData.schedule as {
    members?: { users?: unknown[]; groups?: unknown[] };
    forms?: unknown[];
  };

  return {
    general: {
      ...mainData,
      forms: schedule?.forms || [],
    },
    members: {
      users: schedule?.members?.users || [],
      groups: schedule?.members?.groups || [],
    },
    eventTypes: [],
    reception: null,
    timeline: null,
  };
};

export interface SortableExecution {
  id: string;
  divId: string;
  status?: string;
  priority: number;
  startTz: Date;
  endTz: Date;
  triggersProgress: number;
  timeLeft: number;
  hasExtraTime: boolean;
  endExtraTime: number;
  endTimeWithExtra: number;
  endTime: number;
  scheduleCreatedAt: Date;
  executionCreatedAtTz: Date;
  executionEndAtTz: string | Date;
  divisionName?: string;
  scheduleName?: string;
  triggers?: unknown[];
  triggerCompletition?: {
    finishedPercentage: number;
  };
  deliveryState?: {
    viewed: unknown[];
    delivered: unknown[];
  };
  schedule?: {
    name?: string;
    id?: string;
    duration?: number;
    members?: {
      users?: unknown[];
      groups?: unknown[];
    };
    taskSummary?: {
      id?: string;
      name?: string;
    };
    schedule?: {
      timeZone?: string;
    };
  };
  division?: {
    id?: string;
    name?: string;
  };
  organization?: {
    id: string;
  };
}

export const sortingExecutions = <T extends SortableExecution>(data: T[]): T[] =>
  data.sort((a, b) => {
    const dateA = new Date(a.executionEndAtTz);
    const dateB = new Date(b.executionEndAtTz);

    if (dateA.getTime() !== dateB.getTime()) {
      return dateB.getTime() - dateA.getTime();
    }

    return a.priority - b.priority;
  });

export const WHEN_SHOULD_OCCUR_OPTIONS = [
  {
    label: 'Inmediatamente',
    value: WHEN_SHOULD_OCCUR_VALUES.IMMEDIATELY,
  },
  {
    label: 'Fecha y hora personalizada',
    value: WHEN_SHOULD_OCCUR_VALUES.CUSTOM,
  },
];
